'use client';

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { captureEvent, EVENTS } from '@/lib/posthog/events';
import { useAccess } from '@/hooks/useAccess';
import { useOddsBoard } from '@/hooks/useOddsBoard';
import { accessStateQuery, useAccessState, useAwaitAccess } from '../../hooks/useAccessState';
import { useSession } from '../../hooks/useSession';
import type { ProductWithPricing, Sport } from '@sharpr/types';
import { cn } from '@sharpr/ui';
import { PlayerSelectionPanel, formatAvailabilityTime } from './PlayerSelectionPanel';
import { adminSummary } from './adminSummary';
import { SelectedPlayersTable } from './SelectedPlayersTable';
import { DownloadMenu } from './DownloadMenu';
import { EmptySelectionState } from './EmptySelectionState';
import { AddSportDialog } from './AddSportDialog';
import { OddsViewSkeleton } from './OddsViewSkeleton';
import { usePlayerSelection } from '../../hooks/usePlayerSelection';
import { useDebounce } from '../../hooks/useDebounce';
import { useSport } from '../../contexts/SportContext';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { recalculatePlayerBestLines } from '../../utils/bestLineCalculator';
import { stripForPublicView, stripProjectionData, withHiddenBy } from '../../utils/stripProjectionData';
import { isWithinSeasonCtaWindow } from '../../services/billing/seasonClamp';
import type { SelectablePlayer } from '../../types/selection';
import type { MarketCode, SortField } from '@sharpr/types';
import type { SortDirection } from '@sharpr/ui';
import { sortByBestEV } from '../../utils/sorting';
import { teamNames, fullTeamNames, teamCities } from '../../constants/teamNames';
import type { OverlayTarget, ResearchTarget } from '../../types/research';
import { EvTeaseProvider } from '../../contexts/EvTeaseContext';
import { useSlipDeepLink } from '../../hooks/useSlipDeepLink';
import { buildOverlayTarget } from '../../utils/buildOverlayTarget';
import { aggregateMarketLines, buildResearchTarget, resolveSwapTarget } from '@/lib/research/buildResearchTarget';
import { resolvePositionType } from '@/utils/playerTaxonomy';
import { getMarketName } from '@sharpr/types';
import { bestLineFloor, DEFAULT_BEST_LINE_MIN_LIQUIDITY, type BestLineEligibility } from '@sharpr/pricing';
import { useBookCatalog } from '@/hooks/useBookCatalog';
import { EyeOff } from 'lucide-react';
import { useOptionalTour } from './tour/TourContext';
import {
  UpgradePromptAnalyticsProvider,
  upgradeViewerOf,
  useUpgradePromptTracker,
} from './UpgradePromptAnalytics';

// Lazy (12 tab modules + Recharts); kept mounted after open so Vaul's exit animation plays.
const PlayerResearchOverlay = dynamic(
  () => import('./research/PlayerResearchOverlay').then((m) => ({ default: m.PlayerResearchOverlay })),
  { ssr: false },
);

// Lazy (~200 KB Recharts). One view-level mount shared by both viewports + deep-link.
const LineMovementOverlay = dynamic(
  () => import('./LineMovementOverlay').then((m) => ({ default: m.LineMovementOverlay })),
  { ssr: false },
);


interface PlayerSelectionViewProps {
  sportsbooks: string[];
  className?: string;
  onReorderSportsbooks?: (newOrder: string[]) => void;
}

// Stable, so memoized pricing doesn't rerun on every render before preferences load.
const NO_BOOKS: string[] = [];

export function PlayerSelectionView({
  sportsbooks,
  className,
  onReorderSportsbooks
}: PlayerSelectionViewProps) {
  const selectedSport = useSport();
  const router = useRouter();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const { accessState, loading: accessStateLoading } = useAccessState();
  // The viewer's tier for this sport: the claim at first paint (no network), the live status once loaded. It picks the
  // endpoint; the server cuts the data. The fetch waits only for the (fast, local) session.
  const access = useAccess();
  const tier = access.tier(selectedSport);
  // The cut this viewer asks for: an admin previewing the subscriber view gets the subscriber cut.
  const view = access.view(selectedSport);
  const adminView = view === 'admin';
  const [researchTarget, setResearchTarget] = useState<ResearchTarget | null>(null);
  // Mount on first open, then keep mounted so the lazy chunk + Vaul exit animation persist.
  const [overlayMounted, setOverlayMounted] = useState(false);
  // No client-side dedup — raw stream lets analytics answer repeat-opens analyst-side.
  const openResearch = useCallback((t: ResearchTarget) => {
    setOverlayMounted(true);
    setResearchTarget(t);
    captureEvent(EVENTS.RESEARCH_OVERLAY_OPENED, {
      sport: selectedSport,
      player_id: t.playerId,
      player_name: t.playerName,
      team: t.team,
      market: t.marketCode ?? null,
      surface: 'props_grid',
    });
  }, [selectedSport]);

  // Line-movement overlay state — shared by both viewports + the share-slip deep-link.
  const [lineOverlayTarget, setLineOverlayTarget] = useState<OverlayTarget | null>(null);
  const [lineOverlayMounted, setLineOverlayMounted] = useState(false);
  // openLineOverlay is defined below, after viewerHasProjectionAccess resolves.

  const {
    selectedIds,
    togglePlayer,
    selectAll,
    selectPlayers,
    clearAll,
    isPanelCollapsed,
    togglePanel
  } = usePlayerSelection();
  // The questionable players the viewer has out, per game (boardPlayerKey). A game prices at the scenario they make (spec §4.4).
  const [outQuestionable, setOutQuestionable] = useState<ReadonlySet<string>>(new Set());
  const toggleOut = useCallback((key: string) => setOutQuestionable(prev => {
    const next = new Set(prev);
    if (!next.delete(key)) next.add(key);
    return next;
  }), []);
  const clearOut = useCallback(() => setOutQuestionable(new Set()), []);

  // User preferences — client-side hide list applied via allPlayers; distinct from
  // the admin `hiddenMarkets` system setting below (which strips server-side).
  const { preferences } = useUserPreferences();
  const excludedFromBestLine = preferences?.excluded_from_best_line ?? NO_BOOKS;
  const userHiddenMarketsForSport = useMemo(() => new Set<MarketCode>(
    preferences?.sportPreferences?.[selectedSport]?.hidden_markets ?? []
  ), [preferences?.sportPreferences, selectedSport]);

  // Session-only, like the liquidity tiers: an exchange side with less liquidity (USD) never wins a best line.
  const [minBestLineLiquidity, setMinBestLineLiquidity] = useState(DEFAULT_BEST_LINE_MIN_LIQUIDITY);
  const catalog = useBookCatalog();
  // What may win a best line, for both sports' pricers (the only readers): keyed by content, so an equal exclusion list
  // re-prices nothing.
  const excludedKey = excludedFromBestLine.join('\n');
  const eligibility = useMemo<BestLineEligibility>(() => ({
    excluded: new Set(excludedKey ? excludedKey.split('\n') : []),
    floor: bestLineFloor(minBestLineLiquidity, catalog.values()),
  }), [excludedKey, minBestLineLiquidity, catalog]);

  const { data, error, refetch, questionable } = useOddsBoard({ sport: selectedSport, enabled: !access.loading, tier: view, bestLineEligibility: eligibility, outQuestionable });

  // What the server sent wins once the payload lands; before it, the viewer's tier for the sport.
  const viewerHasProjectionAccess = data ? !data.noSportAccess : tier !== 'public';

  // Line movement is subscriber-only: non-entitled clicks route to sign-up.
  const openLineOverlay = useCallback(
    (t: OverlayTarget, surface: 'props_grid' | 'deep_link' = 'props_grid') => {
      if (!viewerHasProjectionAccess) {
        router.push(`/signup?redirect=/${selectedSport}`);
        return;
      }
      setLineOverlayMounted(true);
      setLineOverlayTarget(t);
      captureEvent(EVENTS.LINE_MOVEMENT_OPENED, {
        sport: selectedSport,
        player_id: t.playerId ?? null,
        player_name: t.playerName,
        team: t.team ?? null,
        market: t.marketCode ?? t.market,
        book: t.clickedBook ?? null,
        best_ev: t.edge?.ev ?? null,
        best_line: t.edge?.line ?? null,
        best_side: t.edge?.side ?? null,
        surface,
      });
    },
    [viewerHasProjectionAccess, selectedSport, router],
  );
  // Single-arg variant for the share-slip deep-link consumer (tags surface as deep_link).
  const openLineOverlayFromDeepLink = useCallback(
    (t: OverlayTarget) => openLineOverlay(t, 'deep_link'),
    [openLineOverlay],
  );

  // Extract hidden lists (only present for admins), stabilized for memoization
  const hiddenTeamsKey = data?.hiddenTeams?.join(',') ?? '';
  const hiddenMarketsKey = data?.hiddenMarkets?.join(',') ?? '';
  const hiddenTeams = useMemo(() => data?.hiddenTeams, [hiddenTeamsKey]);
  const hiddenMarkets = useMemo(() => data?.hiddenMarkets, [hiddenMarketsKey]);

  // Overlay swap (e.g. batter Matchup tab → opposing pitcher overlay). Prefer
  // a lookup into the full unfiltered player list so the new target carries
  // canonical markets/projections/lineup; fall back to the overlay-supplied
  // minimal target only when the player isn't in the current odds payload.
  const handleSwapTarget = useCallback((req: { playerId: string; fallback: ResearchTarget }) => {
    const allPlayers = data?.players ?? [];
    openResearch(resolveSwapTarget(req, (id) => allPlayers.find((p) => p.id === id)));
  }, [data?.players, openResearch]);

  // NBA's seed is cut per view and its admin rows say per market why subscribers don't see it (hiddenBy). MLB's payload
  // is the admin cut either way, with its hidden teams and markets beside the rows, until phase 2 moves it onto the
  // seed: the admin view stamps them onto each market's hiddenBy like NBA's, and the preview mirrors the server's
  // subscriber strip (stripForPublicView keeps client and server in lockstep). bestMarket is rebuilt by the strip.
  const viewPlayers = useMemo(() => {
    const raw = data?.players ?? [];
    if (selectedSport === 'nba') return raw;
    return adminView
      ? withHiddenBy(raw, hiddenTeams ?? [], hiddenMarkets ?? [])
      : stripForPublicView(data?.adminPreview?.projectionsHidden ? stripProjectionData(raw) : raw,
        { hiddenTeams: hiddenTeams ?? [], hiddenMarkets: hiddenMarkets ?? [] });
  }, [data?.players, selectedSport, adminView, hiddenTeams, hiddenMarkets, data?.adminPreview?.projectionsHidden]);

  // User-preference market hide (cosmetic, per-user).
  const allPlayers = useMemo(() => {
    if (userHiddenMarketsForSport.size === 0) return viewPlayers;
    const stripHidden = <T extends Record<string, unknown>>(ms: T): T => {
      const out = {} as Record<string, unknown>;
      for (const [m, d] of Object.entries(ms)) {
        if (!userHiddenMarketsForSport.has(m as MarketCode)) out[m] = d;
      }
      return out as T;
    };
    const keepBestMarket = <T extends { market: MarketCode }>(bm: T | undefined): T | undefined =>
      bm && !userHiddenMarketsForSport.has(bm.market) ? bm : undefined;
    return viewPlayers.map((p) => ({
      ...p,
      markets: stripHidden(p.markets),
      bestMarket: keepBestMarket(p.bestMarket),
    }));
  }, [viewPlayers, userHiddenMarketsForSport]);

  // The admin view's banner, from the rows before the viewer's own market hides. Null in the preview (nothing extra
  // shows) or when nothing is hidden.
  const banner = useMemo(() => (adminView && data
    ? adminSummary({ rows: viewPlayers, constructionMode: !!data.adminPreview?.projectionsHidden, releaseTime: data.projectionsAvailableAt })
    : null), [adminView, data, viewPlayers]);

  // Refs let analytics wrappers read latest data without re-creating callback identity on
  // each refetch — preserves PlayerSelectionPanel memoization.
  const allPlayersRef = useRef(allPlayers);
  useEffect(() => { allPlayersRef.current = allPlayers; }, [allPlayers]);
  const selectedIdsRef = useRef<Set<string>>(selectedIds);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  const handleTogglePlayer = useCallback((playerId: string) => {
    const wasSelected = selectedIdsRef.current.has(playerId);
    togglePlayer(playerId);
    const player = allPlayersRef.current.find((p) => p.id === playerId);
    if (player) {
      captureEvent(EVENTS.PLAYER_SELECTED, {
        action: wasSelected ? 'remove' : 'add',
        sport: selectedSport,
        player_id: player.id,
        player_name: player.name,
        team: player.team,
        best_market: player.bestMarket?.market,
        best_ev: player.bestMarket?.ev ?? null,
        surface: 'props_grid',
      });
    }
  }, [togglePlayer, selectedSport]);

  const handleSelectAll = useCallback((playerIds: string[]) => {
    selectAll(playerIds);
    captureEvent(EVENTS.SELECT_ALL_CLICKED, {
      sport: selectedSport,
      count: playerIds.length,
      surface: 'props_grid',
    });
  }, [selectAll, selectedSport]);

  const handleClearAll = useCallback(() => {
    captureEvent(EVENTS.CLEAR_ALL_CLICKED, {
      sport: selectedSport,
      count_before: selectedIdsRef.current.size,
      surface: 'props_grid',
    });
    clearAll();
  }, [clearAll, selectedSport]);

  // Auto-select all on first load; on refetch select only genuinely new players
  // (knownPlayerIds guards against re-selecting user-deselected ones).
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const knownPlayerIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (allPlayers.length > 0) {
      const currentIds = allPlayers.map(p => p.id);

      if (!hasAutoSelected) {
        selectAll(currentIds);
        knownPlayerIds.current = new Set(currentIds);
        setHasAutoSelected(true);
      } else {
        const newIds = currentIds.filter(id => !knownPlayerIds.current.has(id));
        if (newIds.length > 0) {
          selectPlayers(newIds);
        }
        knownPlayerIds.current = new Set(currentIds);
      }
    }
  }, [hasAutoSelected, allPlayers, selectAll, selectPlayers]);

  const projectionsLastUpdated = data?.projectionsLastUpdated;
  // Hidden = server-stripped (non-admin) OR admin previewing with the setting on.
  const projectionsHidden =
    (data?.projectionsHidden ?? false) ||
    (!adminView && !!data?.adminPreview?.projectionsHidden);
  const projectionsAvailableAt =
    data?.projectionsAvailableAt ?? data?.adminPreview?.projectionsAvailableAt;
  const noSportAccess = data?.noSportAccess ?? false;

  // Products for the upgrade banner — only when sport access is missing.
  const { data: productsData } = useQuery({
    queryKey: ['products-for-upgrade'],
    queryFn: async () => {
      const res = await fetch('/api/billing/products');
      const json = await res.json();
      return (json.data?.products || []) as ProductWithPricing[];
    },
    enabled: !!user && noSportAccess,
    staleTime: 5 * 60 * 1000,
  });

  const upgradeProduct = useMemo(() => {
    if (!productsData || !noSportAccess) return null;
    const currentSports = new Set(accessState.sportsAccess);
    const candidates = productsData
      .filter(p =>
        p.is_active && !!p.stripe_monthly_price_id && p.sport != null && !currentSports.has(p.sport) &&
        // Off-season sports now flow through /api/billing/products; don't offer to add one that
        // can't be subscribed (checkout-setup would 409 season_ended).
        isWithinSeasonCtaWindow(accessState.sportSeasons.find(s => s.sport === p.sport)));
    return candidates[0] ?? null;
  }, [productsData, noSportAccess, accessState.sportsAccess, accessState.sportSeasons]);

  // Season-aware: prompt only when season is in progress or within the lead window (missing
  // data fails open). Logged-in only — anon gets the sign-up banner (add-sport needs a session).
  const seasonForSport = accessState.sportSeasons.find((s) => s.sport === selectedSport);
  const showUpgradeCta =
    !!user &&
    noSportAccess &&
    accessState.billingHealth !== 'failing' &&
    isWithinSeasonCtaWindow(seasonForSport);

  // Add-sport requires an existing subscriber; users with no live subs (fresh trial-eligible
  // or churned) go to /pricing for trial signup / resubscribe. Existing subscribers open the
  // saved-card AddSportDialog (which itself falls back to /checkout/pending if the customer
  // has no card on file), rather than bouncing straight to the redirect checkout.
  const hasAnySub = accessState.sportsAccess.length > 0;
  const [addDialogSport, setAddDialogSport] = useState<Sport | null>(null);
  // A just-added sport's access row lands via webhook: await it (its tier change then reloads the board).
  const { awaitChange: awaitAddedSport } = useAwaitAccess<Sport>((state, sport) => state.sportsAccess.includes(sport));
  const handleUpgrade = () => {
    if (!hasAnySub) {
      window.location.href = '/pricing';
      return;
    }
    if (upgradeProduct?.sport) setAddDialogSport(upgradeProduct.sport);
  };
  const upgradeCtaLabel = !hasAnySub
    ? accessState.trialUsed
      ? 'Subscribe'
      : 'Start trial'
    : `Add ${upgradeProduct?.name ?? 'Subscription'}`;
  const upgradeCtaDisabled = hasAnySub && !upgradeProduct;

  const upgradeTracker = useUpgradePromptTracker(
    selectedSport,
    upgradeViewerOf(!!user, accessState.sportsAccess.length),
  );
  // Wait for the subscription to load: before that, sportsAccess is [] (wrong viewer) and
  // the banner may still disappear — and only the first view per sport is recorded.
  useEffect(() => {
    if (showUpgradeCta && !accessStateLoading) upgradeTracker.trackView('sport_banner');
  }, [showUpgradeCta, accessStateLoading, upgradeTracker]);

  // Table sort state — default best_ev desc.
  const [sortField, setSortField] = useState<SortField | null>('best_ev');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (field === 'best_ev') {
        // best_ev toggles asc/desc; book columns return to best_ev.
        const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
        setSortDirection(newDirection);
      } else {
        setSortField('best_ev');
        setSortDirection('desc');
      }
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const [selectedMarkets, setSelectedMarkets] = useState<Set<MarketCode>>(new Set());
  const [selectedTeams, setSelectedTeams] = useState<Set<string>>(new Set());

  // Reset filters when sport changes
  useEffect(() => {
    setSelectedMarkets(new Set());
    setSelectedTeams(new Set());
    setSearchQuery('');
    setOutQuestionable(new Set());
  }, [selectedSport]);

  // Drop any selected market that became user-hidden (toggled off in Settings while
  // the chip was active). Mirrors the team cleanup in PlayerSelectionPanel.
  useEffect(() => {
    if (userHiddenMarketsForSport.size === 0 || selectedMarkets.size === 0) return;
    const filtered = Array.from(selectedMarkets).filter((m) => !userHiddenMarketsForSport.has(m));
    if (filtered.length !== selectedMarkets.size) {
      setSelectedMarkets(new Set(filtered));
    }
  }, [userHiddenMarketsForSport, selectedMarkets]);

  // Search state (shared between both panels)
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 200);

  // Session-only filter state — drives the left panel's min-EV chip gate and the
  // odds table's row min-EV + liquidity dot colors.
  const [minEVThreshold, setMinEVThreshold] = useState(0);
  const [liquidityThresholds, setLiquidityThresholds] = useState({
    orange: 1,
    yellow: 100,
    green: 200,
  });

  // MLB-only "confirmed lineups only" filter. localStorage-backed; hydrates after mount to
  // avoid SSR mismatch. Owned here so both panels apply the same gate.
  const [mlbConfirmedOnly, setMlbConfirmedOnly] = useState(false);
  useEffect(() => {
    try {
      setMlbConfirmedOnly(localStorage.getItem('mlb_confirmed_lineups_only') === 'true');
    } catch {
      // localStorage unavailable — leave default
    }
  }, []);
  const handleMlbConfirmedOnlyChange = useCallback((next: boolean) => {
    setMlbConfirmedOnly(next);
    try {
      localStorage.setItem('mlb_confirmed_lineups_only', String(next));
    } catch {
      // ignore
    }
  }, []);

  // Heavy compute (best lines, totals, sort). Independent of `selectedIds`, so a checkbox click doesn't redo it for
  // ~350 players; layered with the light projection below.
  const recomputedPlayers = useMemo(() => {
    if (allPlayers.length === 0) return [] as Omit<SelectablePlayer, 'isSelected'>[];

    const players = allPlayers.map(player => {
      // NBA rows are priced with the user's exclusions and OUT chips already applied (useNbaBoard).
      const priced = selectedSport === 'nba' ? player : recalculatePlayerBestLines(player, eligibility);
      return { ...priced, totalMarkets: Object.keys(priced.markets).length, averageMinutes: priced.projectedMinutes ?? 0 };
    });

    return players.sort((a, b) => sortByBestEV(a, b, 'desc'));
  }, [allPlayers, eligibility, selectedSport]);

  // The board's downloads (NBA: the projection core), the cut this viewer asks for; always there (a viewer who sees no
  // projections gets the file's columns, no rows).
  const downloadMenu = selectedSport === 'nba' ? (
    <DownloadMenu viewQuery={view !== tier ? `?view=${view}` : ''} out={[...outQuestionable]} />
  ) : null;

  // Walkthrough demo handlers — registered via TourContext (keeps tour steps declarative);
  // they open the real overlay on a top-EV player.
  const tour = useOptionalTour();
  useEffect(() => {
    if (!tour) return;
    const pickPlayerWithBestMarket = () =>
      recomputedPlayers.find((p) => p.bestMarket && p.markets[p.bestMarket.market]?.books?.length) ?? null;
    const pickPlayerForResearch = () =>
      recomputedPlayers.find((p) => {
        if (selectedSport !== 'mlb') return true;
        return resolvePositionType(p.position, p.bestMarket?.market) === 'pitcher';
      }) ?? null;

    return tour.registerDemoHandlers({
      openLineMovementDemo: () => {
        const player = pickPlayerWithBestMarket();
        if (!player || !player.bestMarket) return;
        const marketData = player.markets[player.bestMarket.market];
        if (!marketData) return;
        const pos = player.position;
        openLineOverlay(
          buildOverlayTarget({
            playerName: player.name,
            market: getMarketName(player.bestMarket.market),
            marketCode: player.bestMarket.market,
            imageUrl: player.imageUrl ?? undefined,
            playerId: player.id,
            projection: marketData.projection ?? null,
            bestLines: marketData.bestLines,
            books: marketData.books,
            projectedMinutes: marketData.projectedMinutes,
            pmf: marketData.pmf,
            outPlayerIds: player.scenario?.outPlayerIds,
            team: player.team,
            opponent: player.game?.opp,
            commenceTime: player.game?.commenceTime,
            gameId: player.game?.id,
            isHome: player.game?.isHome,
            position: pos,
            positionType: resolvePositionType(pos, player.bestMarket.market),
            teamNotes: player.teamNotes,
            playerNotes: player.playerNotes,
            lineup: player.lineup,
            marketProjections: aggregateMarketLines(player).marketProjections,
          }),
        );
      },
      openResearchDemo: () => {
        const player = pickPlayerForResearch();
        if (!player) return;
        openResearch(buildResearchTarget(player, { bestMarket: player.bestMarket }));
      },
      closeOverlays: () => {
        setLineOverlayTarget(null);
        setResearchTarget(null);
      },
    });
  }, [tour, recomputedPlayers, selectedSport, openLineOverlay, openResearch]);

  // Share-slip deep-link consumer — URL params + localStorage feed one flow (see useSlipDeepLink).
  const selectFirstPlayer = useCallback((playerId: string) => selectPlayers([playerId]), [selectPlayers]);
  useSlipDeepLink({
    players: recomputedPlayers,
    sport: selectedSport,
    noSportAccess,
    userHiddenMarkets: userHiddenMarketsForSport,
    selectPlayer: selectFirstPlayer,
    openOverlay: openLineOverlayFromDeepLink,
  });

  // Light projection — re-runs only on selection change, skipping the heavy compute above.
  // Maps just the selected subset (a handful) rather than the full ~350-player list.
  const selectedPlayers = useMemo<SelectablePlayer[]>(() => {
    return recomputedPlayers
      .filter(p => selectedIds.has(p.id))
      .map(p => ({ ...p, isSelected: true })) as SelectablePlayer[];
  }, [recomputedPlayers, selectedIds]);

  // Filter selected by MLB confirmed-lineups gate + search. Gate mirrors PlayerSelectionPanel;
  // TWP-aware (every market with `lineup` must be confirmed).
  const filteredSelectedPlayers = useMemo(() => {
    const applyMlbConfirmedGate = mlbConfirmedOnly && selectedSport === 'mlb';

    const passesConfirmedGate = (player: SelectablePlayer): boolean => {
      if (!applyMlbConfirmedGate) return true;
      const marketsWithLineup = Object.values(player.markets).filter(m => m?.lineup);
      return marketsWithLineup.length > 0
        ? marketsWithLineup.every(m => m!.lineup!.status === 'Confirmed')
        : player.lineup?.status === 'Confirmed';
    };

    const query = debouncedSearchQuery?.toLowerCase();
    if (!query && !applyMlbConfirmedGate) return selectedPlayers;

    return selectedPlayers.filter(player => {
      if (!passesConfirmedGate(player)) return false;
      if (!query) return true;

      if (
        player.name.toLowerCase().includes(query) ||
        player.team.toLowerCase().includes(query) ||
        player.game?.opp?.toLowerCase().includes(query) ||
        player.position?.toLowerCase().includes(query)
      ) {
        return true;
      }

      // Full team names + cities (own team, then opponent below)
      const teamName = teamNames[player.team]?.toLowerCase();
      const fullTeamName = fullTeamNames[player.team]?.toLowerCase();
      const cityName = teamCities[player.team]?.toLowerCase();

      if (
        (teamName && teamName.includes(query)) ||
        (fullTeamName && fullTeamName.includes(query)) ||
        (cityName && cityName.includes(query))
      ) {
        return true;
      }

      if (player.game?.opp) {
        const oppTeamName = teamNames[player.game.opp]?.toLowerCase();
        const oppFullTeamName = fullTeamNames[player.game.opp]?.toLowerCase();
        const oppCityName = teamCities[player.game.opp]?.toLowerCase();

        if (
          (oppTeamName && oppTeamName.includes(query)) ||
          (oppFullTeamName && oppFullTeamName.includes(query)) ||
          (oppCityName && oppCityName.includes(query))
        ) {
          return true;
        }
      }

      return false;
    });
  }, [selectedPlayers, debouncedSearchQuery, mlbConfirmedOnly, selectedSport]);

  const noGames = (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-text-primary mb-2">
          No Upcoming {selectedSport.toUpperCase()} Games
        </h3>
        <p className="text-sm text-muted-foreground dark:text-text-secondary">
          Check back when games are scheduled
        </p>
      </div>
    </div>
  );

  // A loaded board with no players (the NBA board after the night's last tip): nothing to auto-select, so no skeleton.
  if (!error && data && data.players.length === 0) return noGames;

  // Layout-shaped skeleton until data loads + players auto-select — paints the
  // page structure immediately instead of a centered splash logo.
  if (!hasAutoSelected && !error) {
    return <OddsViewSkeleton className={className} sport={selectedSport} />;
  }

  if (error) {
    const isNoGamesError = error.message?.toLowerCase().includes('no odds data') ||
                          error.message?.toLowerCase().includes('no games');

    if (isNoGamesError) return noGames;

    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-600 dark:text-data-negative mb-2">
            Failed to Load Odds Data
          </h3>
          <p className="text-sm text-muted-foreground dark:text-text-secondary mb-4">
            {error.message}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <EvTeaseProvider value={data?.noSportAccess === true}>
    <UpgradePromptAnalyticsProvider tracker={upgradeTracker}>
    <div className={cn(
      "flex flex-col h-full overflow-hidden",
      className
    )}>
      {/* Slim upgrade banner for sport-without-access — odds-only table still renders below. */}
      {showUpgradeCta && (
        <div className="flex items-center gap-3 md:gap-4 px-4 sm:px-5 py-2.5 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent border-b border-blue-500/20">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <p className="text-sm text-gray-700 dark:text-text-secondary">
              <span className="md:hidden">Unlock +EV edges</span>
              <span className="hidden md:inline">
                {!hasAnySub
                  ? accessState.trialUsed
                    ? <>Subscribe to unlock {selectedSport.toUpperCase()} projections and +EV edges.</>
                    : <>Start your free trial to unlock {selectedSport.toUpperCase()} projections and +EV edges.</>
                  : upgradeProduct
                    ? <>Unlock {selectedSport.toUpperCase()} projections and +EV edges — <span className="font-semibold text-gray-900 dark:text-text-primary">${(upgradeProduct.monthly_price_cents / 100).toFixed(0)}/mo</span></>
                    : 'Subscribe to see projections and EV calculations for this sport.'}
              </span>
            </p>
          </div>
          <button
            onClick={() => {
              upgradeTracker.trackClick('sport_banner', 'cta', hasAnySub ? 'add_sport_dialog' : '/pricing');
              handleUpgrade();
            }}
            disabled={upgradeCtaDisabled}
            className="px-3.5 py-1.5 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all shadow-sm hover:shadow flex items-center justify-center gap-2 flex-shrink-0"
          >
            {upgradeCtaLabel}
          </button>
        </div>
      )}

      {addDialogSport && (
        <AddSportDialog
          sport={addDialogSport}
          onClose={() => setAddDialogSport(null)}
          onAdded={() => {
            const s = addDialogSport;
            setAddDialogSport(null);
            queryClient.invalidateQueries({ queryKey: accessStateQuery(user?.id).queryKey });
            if (s) awaitAddedSport(s);
          }}
        />
      )}

      {/* Admin denotation: everything on this screen only an admin can see.
          Specific hides also get per-row/cell eye-slash markers downstream. */}
      {banner && (
        <div role="status" className="flex-shrink-0 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <EyeOff className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            Admin view
          </span>
          {banner.constructionMode && <span>Construction mode — ALL EV/projections hidden from subscribers</span>}
          {banner.teams.length > 0 && <span>Hidden teams: {[...banner.teams].sort().join(', ')}{banner.releaseTime && ` (release ${formatAvailabilityTime(banner.releaseTime)} ET)`}</span>}
          {banner.markets.length > 0 && <span>Hidden markets: {[...banner.markets].sort().map(market => getMarketName(market as MarketCode)).join(', ')}</span>}
          {banner.playerHides > 0 && <span>{banner.playerHides} analyst-hidden player market{banner.playerHides === 1 ? '' : 's'} (EV admin-only; subscribers get odds-only)</span>}
        </div>
      )}

      {/* Odds-only table renders for everyone; banner above prompts for projections/EV. */}
      <div className="flex-1 flex overflow-hidden">
        <PlayerSelectionPanel
          players={recomputedPlayers}
          selectedIds={selectedIds}
          isCollapsed={isPanelCollapsed}
          onTogglePlayer={handleTogglePlayer}
          onToggleCollapse={togglePanel}
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
          selectedMarkets={selectedMarkets}
          onMarketsChange={setSelectedMarkets}
          selectedTeams={selectedTeams}
          onTeamsChange={setSelectedTeams}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          lastUpdated={projectionsLastUpdated}
          download={downloadMenu}
          projectionsHidden={projectionsHidden && !noSportAccess}
          projectionsAvailableAt={projectionsAvailableAt}
          selectedSportsbooks={sportsbooks}
          questionablePlayers={questionable}
          outQuestionable={outQuestionable}
          onToggleOut={toggleOut}
          onClearOut={clearOut}
          onOpenResearch={openResearch}
          onLineMovementClick={openLineOverlay}
          minEVThreshold={minEVThreshold}
          onMinEVThresholdChange={setMinEVThreshold}
          liquidityThresholds={liquidityThresholds}
          onLiquidityThresholdsChange={setLiquidityThresholds}
          minBestLineLiquidity={minBestLineLiquidity}
          onMinBestLineLiquidityChange={setMinBestLineLiquidity}
          confirmedLineupsOnly={mlbConfirmedOnly}
          onConfirmedLineupsOnlyChange={handleMlbConfirmedOnlyChange}
          className="md:border-r border-gray-200 dark:border-border-primary"
        />

      {/* Right panel (desktop only). `@container` lets density rules query pane width via
          @md:/@lg: — the pane swings ~270px on filter collapse, invisible to viewport queries. */}
      <div className="@container hidden md:flex flex-1 flex-col min-w-0">
        {selectedPlayers.length > 0 ? (
          <SelectedPlayersTable
            selectedPlayers={filteredSelectedPlayers}
            sportsbooks={sportsbooks}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onReorderSportsbooks={onReorderSportsbooks}
            selectedMarkets={selectedMarkets}
            selectedTeams={selectedTeams}
            isPanelCollapsed={isPanelCollapsed}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sport={selectedSport}
            onOpenResearch={openResearch}
            onLineMovementClick={openLineOverlay}
            liquidityThresholds={liquidityThresholds}
            minEVThreshold={minEVThreshold}
            className="flex-1"
          />
        ) : (
          <EmptySelectionState
            isPanelCollapsed={isPanelCollapsed}
            onExpandPanel={isPanelCollapsed ? togglePanel : undefined}
          />
        )}
      </div>
      </div>

      {overlayMounted && (
        <PlayerResearchOverlay
          target={researchTarget}
          onClose={() => setResearchTarget(null)}
          sport={selectedSport}
          onSwapTarget={handleSwapTarget}
        />
      )}

      {lineOverlayMounted && (
        <LineMovementOverlay
          target={lineOverlayTarget}
          onClose={() => setLineOverlayTarget(null)}
          sport={selectedSport}
          onOpenResearch={openResearch}
        />
      )}
    </div>
    </UpgradePromptAnalyticsProvider>
    </EvTeaseProvider>
  );
}
